import Store from 'store/store';
import { parseDate } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
import getCopyWithParams from 'utils/getCopyWithParams';

function getReleaseDateFormat(movie, copy = Store.get().appData?.copy?.baseCopy) {
	const releaseDateStr = movie.dates?.release;
	if (!releaseDateStr) return '';

	const releaseDate = parseDate(releaseDateStr);
	const now = new Date();
	const sameYear = now.getFullYear() === releaseDate.getFullYear();

	let dateFormat = copy?.['$RELEASE_DATE_FORMAT_COMPACT'] || 'MMMM d';
	if (/^\d{4}$/.test(movie.dates.release)) {
		dateFormat = copy?.['$RELEASE_DATE_FORMAT_YEAR_ONLY'] || 'yyyy';
	} else if (/^\d{4}-\d{2}$/.test(movie.dates.release)) {
		if (sameYear) {
			dateFormat = copy?.['$RELEASE_DATE_FORMAT_MONTH_ONLY_COMPACT'] || 'MMMM';
		} else {
			dateFormat = copy?.['$RELEASE_DATE_FORMAT_MONTH_ONLY'] || 'MMMM yyyy';
		}
	} else if (!sameYear) {
		dateFormat = copy?.['$RELEASE_DATE_FORMAT'] || 'MMMM d, yyyy';
	}

	return dateFormat;
}

export function getSeoFooterMessaging(cmsData, copy = Store.get().appData?.copy?.baseCopy) {
	// The SEO line must reflect what's actually live on the site, not just which CMS toggles
	// are flipped. The CMS toggle controls whether the page is exposed; the API status
	// (status.theaters / status.homeEnt) is what determines whether the movie has actually
	// reached pre-sale / in-theaters / home-ent. Both must agree before we promote the message.
	const theaterStatus = cmsData?.status?.theaters;
	const homeEntStatus = cmsData?.status?.homeEnt;
	const inPresale = theaterStatus === 'preSale';
	const inTheaters = theaterStatus === 'inTheaters';
	const inHomeEnt = homeEntStatus === 'homeEnt';
	const hasTrailer = !!cmsData?.pages?.videos?.active && Object.values(cmsData?.trailers || {}).some((trailer) => trailer?.url);

	// Coming soon (no trailer, no tickets, no homeEnt)
	let seoMessaging = copy?.['$FOOTER_SEO_COMING_SOON_NO_TRAILER'] || '%TITLE% official movie site. Only In Theaters %DATE%.';

	// Coming soon + has trailer (no tickets, no homeEnt)
	if (hasTrailer) {
		seoMessaging = copy?.['$FOOTER_SEO_COMING_SOON_TRAILER'] || 'Watch the trailer for %TITLE% on the official movie site.';
	}

	// In presale (tickets on sale, no homeEnt)
	if (inPresale) {
		seoMessaging = copy?.['$FOOTER_SEO_PRESALES'] || 'Find showtimes and get tickets for %TITLE% on the official movie site.';
	}

	// In theaters (tickets on sale, no homeEnt)
	if (inTheaters && !inHomeEnt) {
		seoMessaging = copy?.['$FOOTER_SEO_IN_THEATERS'] || 'Find showtimes and get tickets for %TITLE% on the official movie site.';
	}

	// In theaters + homeEnt (tickets on sale, homeEnt available)
	if (inTheaters && inHomeEnt) {
		seoMessaging = copy?.['$FOOTER_SEO_IN_THEATERS_HOMEENT'] || '%TITLE% official movie site. Watch at Home Now. Also In Theaters.';
	}

	// In homeEnt (no tickets, no theaters)
	if (inHomeEnt && !inTheaters) {
		seoMessaging = copy?.['$FOOTER_SEO_HOMEENT'] || '%TITLE% official movie site. Watch at Home Now.';
	}


	const title = cmsData?.title || '';
	const releaseDate = parseDate(cmsData?.dates?.release);
	const dateFormat = getReleaseDateFormat(cmsData, copy);
	
	return getCopyWithParams(
		{ TITLE: title, DATE: formatDateTime(releaseDate, dateFormat) },
		seoMessaging
	);
}
